import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramPrimarySecondary } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { formatNumber } from '../../../utils/format';

const KEY = 'tabai.calc.electricalPrimarySecondary.v1';

type Fields = {
  primaryVoltage: string;
  primaryCurrent: string;
  secondaryVoltage: string;
  secondaryCurrent: string;
};
const EMPTY: Fields = { primaryVoltage: '', primaryCurrent: '', secondaryVoltage: '', secondaryCurrent: '' };

/**
 * Primary/Secondary Transformer — Vₚ × Iₚ = Vₛ × Iₛ
 * Solves for any missing field when the other 3 are supplied.
 */
const ElectricalElectricalSystemsPrimarySecondaryScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) {
      setError('Fill any 3 of the 4 fields to solve for the missing one.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }

    const pv = parseFloat(fields.primaryVoltage);
    const pc = parseFloat(fields.primaryCurrent);
    const sv = parseFloat(fields.secondaryVoltage);
    const sc = parseFloat(fields.secondaryCurrent);

    // Vₚ × Iₚ = Vₛ × Iₛ
    if (fields.secondaryCurrent === '') {
      if (sv === 0) { setError('Secondary voltage cannot be zero.'); return; }
      setSolvedFor('secondaryCurrent');
      set({ secondaryCurrent: formatNumber((pv * pc) / sv, { decimals: 2 }) });
    } else if (fields.secondaryVoltage === '') {
      if (sc === 0) { setError('Secondary current cannot be zero.'); return; }
      setSolvedFor('secondaryVoltage');
      set({ secondaryVoltage: formatNumber((pv * pc) / sc, { decimals: 2 }) });
    } else if (fields.primaryCurrent === '') {
      if (pv === 0) { setError('Primary voltage cannot be zero.'); return; }
      setSolvedFor('primaryCurrent');
      set({ primaryCurrent: formatNumber((sv * sc) / pv, { decimals: 2 }) });
    } else if (fields.primaryVoltage === '') {
      if (pc === 0) { setError('Primary current cannot be zero.'); return; }
      setSolvedFor('primaryVoltage');
      set({ primaryVoltage: formatNumber((sv * sc) / pc, { decimals: 2 }) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.primaryVoltage && { label: 'Primary Voltage', value: fields.primaryVoltage, unit: 'V', emphasis: solvedFor === 'primaryVoltage' },
    fields.primaryCurrent && { label: 'Primary Current', value: fields.primaryCurrent, unit: 'A', emphasis: solvedFor === 'primaryCurrent' },
    fields.secondaryVoltage && { label: 'Secondary Voltage', value: fields.secondaryVoltage, unit: 'V', emphasis: solvedFor === 'secondaryVoltage' },
    fields.secondaryCurrent && { label: 'Secondary Current', value: fields.secondaryCurrent, unit: 'A', emphasis: solvedFor === 'secondaryCurrent' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Primary/Secondary Transformer"
      subtitle="Calculate transformer primary and secondary voltage and current relationships"
      formula={
        <>
          <FormulaLine>Vₚ × Iₚ = Vₛ × Iₛ</FormulaLine>
          <FormulaNote>V = Voltage, I = Current, p = Primary, s = Secondary. Fill 3 of 4 fields.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Vₚ × Iₚ = Vₛ × Iₛ"
      diagram={<DiagramPrimarySecondary />}
      outputSlots={{
        volts: fields.secondaryVoltage ? parseFloat(fields.secondaryVoltage) : undefined,
        amps: fields.secondaryCurrent ? parseFloat(fields.secondaryCurrent) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.volts != null && !fields.primaryVoltage) {
          patch.primaryVoltage = values.volts.toFixed(2);
        }
        if (values.amps != null && !fields.primaryCurrent) {
          patch.primaryCurrent = values.amps.toFixed(2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Primary Voltage" value={fields.primaryVoltage} onChangeText={(v) => set({ primaryVoltage: v })} rightText="V" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Primary Current" value={fields.primaryCurrent} onChangeText={(v) => set({ primaryCurrent: v })} rightText="A" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Secondary Voltage" value={fields.secondaryVoltage} onChangeText={(v) => set({ secondaryVoltage: v })} rightText="V" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Secondary Current" value={fields.secondaryCurrent} onChangeText={(v) => set({ secondaryCurrent: v })} rightText="A" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default ElectricalElectricalSystemsPrimarySecondaryScreen;
